// @flow
import React, {PureComponent} from 'react';
import {compose} from 'redux';
import {FormattedMessage, injectIntl} from 'react-intl';
import {Switch} from '@homeaway/react-form-components';
import QuantitySelector from '@homeaway/react-quantity';
import {triggerSelectUnselectFilterEdapEvent} from '../../utils/filtersEdapUtils';

import './BedroomsFilter.less';

type LabelComponentProps = {
    data: {
        label: string,
        value: string
    }
};
import type {SearchParamsType} from '../../reducers/searchParams';
import type {MessageDescriptor, IntlShape} from 'react-intl';
type OnChangeType = (SearchParamsType) => void;

type Props = {
    intl: IntlShape,
    maxBedrooms: ?number,
    minBedrooms: number,
    onChange: OnChangeType,
    className: string
};

export const LabelComponent = ({data: {label, value}}: LabelComponentProps) => {
    const labelComp =
        label !== '' ? (
            <span className="BedroomsFilter__label">{label}</span>
        ) : null;
    return (
        <div>
            <span className="BedroomsFilter__value">{value}</span>
            {labelComp}
        </div>
    );
};

export const getBedroomLabel = (
    bedroomsCount: number,
    bedroomsExactMatch: boolean,
    formatMessage: (
        messageDescriptor: MessageDescriptor,
        values?: Object
    ) => string
) => {
    const messageDescriptor = {
        id: 'bedroomsFilterLabel',
        defaultMessage:
            '{bedroomsCount, plural, one {Bedroom} other {Bedrooms}}',
        description: 'Label for the amount of bedrooms to filter on'
    };
    if (bedroomsExactMatch) {
        return bedroomsCount === 0
            ? ''
            : formatMessage(messageDescriptor, {bedroomsCount});
    }

    return formatMessage({
        id: 'bedroomsLabel',
        defaultMessage: 'Bedrooms',
        description: 'Label for the amount of bedrooms to filter on'
    });
};

export const getBedroomValue = (
    bedroomsCount: number,
    bedroomsExactMatch: boolean,
    formatMessage: (
        messageDescriptor: MessageDescriptor,
        values?: Object
    ) => string
) => {
    const exactMessageDescriptor = {
        id: 'bedroomsValueExactLabel',
        defaultMessage:
            '{bedroomsCount, plural, =0 {Studio} other {{bedroomsCount}}}',
        description: 'Specifies a studio to filter on'
    };
    const rangeMessageDescriptor = {
        id: 'bedroomsValueLabel',
        defaultMessage:
            '{bedroomsCount, plural, =0 {Any} other {{bedroomsCount}+}}',
        description: 'Specifies any bedroom count to filter on'
    };
    if (bedroomsExactMatch) {
        return formatMessage(exactMessageDescriptor, {bedroomsCount});
    }
    return formatMessage(rangeMessageDescriptor, {bedroomsCount});
};

const exactBedrooms = (props: Props) => props.maxBedrooms === props.minBedrooms;

export class BedroomsFilter extends PureComponent<Props> {
    render() {
        const props = this.props;
        const {formatMessage} = props.intl;

        const handleBedroomCountChange = (
            bedroomsCount: number,
            exactMatch = exactBedrooms(props)
        ) => {
            const {onChange} = props;
            const params = {};

            if (exactMatch) {
                params.maxBedrooms = bedroomsCount;
            } else {
                params.maxBedrooms = null;
            }

            params.minBedrooms = bedroomsCount;

            triggerSelectUnselectFilterEdapEvent({
                eventaction: 'filter selected',
                eventlabel: `bedrooms_${
                    exactMatch ? '' : 'min_'
                }${bedroomsCount}`
            });

            onChange(params);
        };

        const handleBedroomsExactMatchChange = (event) =>
            handleBedroomCountChange(props.minBedrooms, event.target.checked);

        const {minBedrooms, className} = props;
        const bedroomLabel = getBedroomLabel(
            minBedrooms,
            exactBedrooms(props),
            formatMessage
        );
        const bedroomValue = getBedroomValue(
            minBedrooms,
            exactBedrooms(props),
            formatMessage
        );

        return (
            <div
                role="menuitem"
                aria-label={bedroomLabel}
                className={`BedroomsFilter ${className}`}
                data-wdio="BedroomsFilter"
            >
                <div className="BedroomsFilter__QuantitySelector BedroomsFilter--row form-group">
                    <QuantitySelector
                        increaseLabel={formatMessage({
                            id: 'bedroomsFilterIncreaseLabel',
                            defaultMessage: 'Increase bedrooms',
                            description:
                                'Increases the amount of bedrooms' +
                                ' to search for'
                        })}
                        decreaseLabel={formatMessage({
                            id: 'bedroomsFilterDecreaseLabel',
                            defaultMessage: 'Decrease bedrooms',
                            description:
                                'Decreases the amount of bedrooms to search for'
                        })}
                        id={`bedrooms_filter-quantity_${className}`}
                        value={minBedrooms}
                        onIncrement={handleBedroomCountChange}
                        onDecrement={handleBedroomCountChange}
                        max={50}
                        label={LabelComponent}
                        labelData={{value: bedroomValue, label: bedroomLabel}}
                    />
                </div>
                <div className="BedroomsFilter__Switch BedroomsFilter--row form-group">
                    <Switch
                        id={`bedrooms_switch_${className}`}
                        name="exact bedrooms"
                        label={
                            <FormattedMessage
                                defaultMessage="Use exact match for bedrooms"
                                description="Exact match for bedrooms"
                                id="bedroomsExactLabel"
                            />
                        }
                        checked={exactBedrooms(props)}
                        onChange={handleBedroomsExactMatchChange}
                    />
                </div>
            </div>
        );
    }
}

export default compose(injectIntl)(BedroomsFilter);
